/**
 * TaskModal — create / edit a task. When editing, also shows comments and
 * time entries with quick-log. Used by ProjectDetail, GlobalTasks and Notes
 * ("Convert to task").
 */
import React, { useCallback, useEffect, useState } from 'react';
import { toast } from '../../utils/toast';
import { X, Clock, MessageSquare, Trash2, Plus, Send, Paperclip, Play, Download } from 'lucide-react';
import { wsApi, assetUrl } from './wsApi';
import { TASK_STATUS, PRIORITY, initials, avatarColor, AVATAR_COLORS, fmtDate, fmtDuration, toLocalInputValue, localInputToIso, localDateStr, canManageTask, MAX_ENTRY_HOURS, durationInputError, wrapSpinValue, TASK_TITLE_MAX } from './wsConstants';
import { MemberCombobox, personName, byName } from './wsUi';
import { selectChevron } from '../../components/em/ui';
import { confirmDialog } from '../../components/em/confirm';

const EMPTY = {
  title: '', description: '', status: 'todo', priority: 'medium',
  due_date: '', project_id: '',
  // One list for everyone on the task. Each entry carries the bucket they sit
  // in (member_role) and their standing (role) — mirrors ws_task_members.
  people: [],
};

// Which bucket a person is in.
const TASK_BUCKETS = [
  { value: 'assignee', label: 'Assignee' },
  { value: 'collaborator', label: 'Collaborator' },
  { value: 'watcher', label: 'Watcher' },
];

// Broadcast that task-derived data changed (logged time, comment/file counts).
// The Time/Comments/Files tabs mutate data the *parent* renders — project
// "Hours logged", the per-row duration chip — but they can't use onSaved,
// which closes the modal. Pages listen for this and refetch in place.
// TimerWidget fires the same event when a live timer stops.
export const notifyWorkspaceDataChanged = () =>
  window.dispatchEvent(new Event('workspaceDataChanged'));

// `initialValues` pre-fills a NEW task (e.g. Notes → "Convert to task" passes
// { title, description }); ignored when editing an existing task.
export default function TaskModal({ taskId, presetProjectId, projects = [], users = [], initialValues, onClose, onSaved }) {
  const editing = !!taskId;
  const [form, setForm] = useState({
    ...EMPTY,
    ...(!taskId && initialValues ? initialValues : {}),
    project_id: presetProjectId || '',
  });
  const [task, setTask] = useState(null);
  const [loading, setLoading] = useState(editing);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState('details');

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // Only the task's creator (or a holder of "Edit/Delete any task") may change
  // its details; anyone else sees them read-only. Comments, files and time
  // stay open to everyone on the task. The server enforces the same rule.
  const readOnly = editing && !!task && !canManageTask(task);

  // People on the chosen project (ws_project_members user ids). "Add people"
  // offers only these; a quick task with no project can take anyone.
  // null = not loaded / no project → no restriction.
  const [projectMemberIds, setProjectMemberIds] = useState(null);
  useEffect(() => {
    const pid = form.project_id;
    if (!pid) { setProjectMemberIds(null); return undefined; }
    let cancelled = false;
    const fromProp = projects.find((p) => String(p.id) === String(pid));
    const load = Array.isArray(fromProp?.members)
      ? Promise.resolve(fromProp)
      : wsApi.getProject(pid);
    load
      .then((p) => {
        if (!cancelled) setProjectMemberIds(new Set((p?.members || []).map((m) => Number(m.user_id))));
      })
      .catch(() => { if (!cancelled) setProjectMemberIds(null); });
    return () => { cancelled = true; };
  }, [form.project_id, projects]);

  const addableUsers = users.filter(
    (u) =>
      !form.people.some((p) => p.user_id === Number(u.id)) &&
      (!projectMemberIds || projectMemberIds.has(Number(u.id)))
  );

  // Sub-tabs hand back the refetched task. Keep the modal in sync *and* tell
  // the page, so stats like "Hours logged" move without a manual refresh.
  const applyTask = useCallback((updated) => {
    setTask(updated);
    notifyWorkspaceDataChanged();
  }, []);

  useEffect(() => {
    if (!editing) return;
    wsApi.getTask(taskId)
      .then((t) => {
        setTask(t);
        setForm({
          title: t.title || '',
          description: t.description || '',
          status: t.status,
          priority: t.priority,
          due_date: t.due_date ? String(t.due_date).slice(0, 10) : '',
          project_id: t.project_id || '',
          people: (t.members || []).map((m) => ({
            user_id: Number(m.user_id),
            member_role: m.member_role || 'assignee',
            role: m.role || 'member',
          })),
        });
      })
      .catch(() => toast.error('Could not load task'))
      .finally(() => setLoading(false));
  }, [taskId, editing]);

  const titleLength = form.title.trim().length;
  const titleTooLong = titleLength > TASK_TITLE_MAX;

  const save = async () => {
    if (!form.title.trim()) return toast.error('Task title is required');
    if (titleTooLong) return toast.error(`Task title can't be longer than ${TASK_TITLE_MAX} characters.`);
    setSaving(true);
    try {
      const body = { ...form, project_id: form.project_id || null, due_date: form.due_date || null };
      if (editing) await wsApi.updateTask(taskId, body);
      else await wsApi.createTask(body);
      toast.success(editing ? 'Task updated' : 'Task created');
      onSaved();
    } catch (err) {
      const e = err?.response?.data?.error;
      toast.error((typeof e === 'string' ? e : e?.message) || 'Could not save task');
    } finally {
      setSaving(false);
    }
  };

  // Add on first click, remove on second — matches MemberCombobox's toggle
  // contract so clicking a name in the dropdown behaves as expected.
  const togglePerson = (uid) => {
    const id = Number(uid);
    setForm((f) => {
      const has = f.people.some((p) => p.user_id === id);
      return {
        ...f,
        people: has
          ? f.people.filter((p) => p.user_id !== id)
          : [...f.people, { user_id: id, member_role: 'assignee', role: 'member' }],
      };
    });
  };

  const removePersonAt = (idx) =>
    setForm((f) => ({ ...f, people: f.people.filter((_, i) => i !== idx) }));

  // Rows are addressed by index, not user_id: `ws_task_members` is unique on
  // (task_id, user_id, member_role), so the same person legitimately appears in
  // more than one bucket and user_id alone doesn't identify a row.
  const setPersonField = (idx, key, value) =>
    setForm((f) => {
      const next = f.people.map((p, i) => (i === idx ? { ...p, [key]: value } : p));
      // Changing a bucket can collide with another row for the same person —
      // collapse the pair rather than writing a duplicate the DB would reject.
      const edited = next[idx];
      return {
        ...f,
        people: next.filter(
          (p, i) =>
            i === idx ||
            !(p.user_id === edited.user_id && p.member_role === edited.member_role)
        ),
      };
    });

  return (
    <div style={overlay} onClick={onClose}>
      <div style={{ ...modal, maxWidth: 600 }} onClick={(e) => e.stopPropagation()}>
        <div style={modalHead}>
          <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>{editing ? 'Task Details' : 'New Task'}</h3>
          <button onClick={onClose} style={iconBtn}><X size={18} /></button>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
        ) : (
          <>
            {editing && (
              <div style={{ display: 'flex', gap: 4, marginBottom: 16, borderBottom: '1px solid var(--em-border)' }}>
                {[
                  { k: 'details', label: 'Details' },
                  { k: 'comments', label: `Comments${task?.comment_count ? ` (${task.comment_count})` : ''}`, Icon: MessageSquare },
                  { k: 'files', label: `Files${task?.attachments?.length ? ` (${task.attachments.length})` : ''}`, Icon: Paperclip },
                  { k: 'time', label: 'Time', Icon: Clock },
                ].map((t) => (
                  <button key={t.k} onClick={() => setTab(t.k)} style={{
                    padding: '8px 14px', background: 'none', border: 'none', cursor: 'pointer',
                    fontSize: 13, fontWeight: tab === t.k ? 600 : 500,
                    color: tab === t.k ? '#f54b01' : '#6b7280',
                    borderBottom: `2px solid ${tab === t.k ? '#f54b01' : 'transparent'}`, marginBottom: -1,
                  }}>{t.label}</button>
                ))}
              </div>
            )}

            {tab === 'details' && (
              // A disabled fieldset disables every input, select and button in it.
              <fieldset
                disabled={readOnly}
                style={{ border: 0, padding: 0, margin: 0, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 14 }}
              >
                <Field label="Title *">
                  {/* No maxLength: a hard cap would silently cut a pasted
                      title — over the limit, the field says so instead. */}
                  <input
                    style={titleTooLong ? { ...input, ...invalidInput } : input}
                    value={form.title}
                    onChange={(e) => set('title', e.target.value)}
                    placeholder="What needs to be done?"
                    autoFocus={!editing}
                    aria-invalid={titleTooLong}
                  />
                  {!readOnly && (
                    <div
                      role={titleTooLong ? 'alert' : undefined}
                      style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 11.5, color: titleTooLong ? '#b91c1c' : '#9ca3af' }}
                    >
                      <span>
                        {titleTooLong
                          ? `Task title can't be longer than ${TASK_TITLE_MAX} characters — remove ${titleLength - TASK_TITLE_MAX}.`
                          : ''}
                      </span>
                      <span style={{ whiteSpace: 'nowrap', fontWeight: titleTooLong ? 600 : 400 }}>
                        {titleLength}/{TASK_TITLE_MAX}
                      </span>
                    </div>
                  )}
                </Field>
                <Field label="Description">
                  <textarea style={{ ...input, minHeight: 64, resize: 'vertical' }} value={form.description} onChange={(e) => set('description', e.target.value)} />
                </Field>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Project">
                    <select style={input} value={form.project_id || ''} onChange={(e) => set('project_id', e.target.value)}>
                      <option value="">— Quick task (no project) —</option>
                      {byName(projects).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                    </select>
                  </Field>
                  <Field label="Due date">
                    <input type="date" style={input} value={form.due_date} onChange={(e) => set('due_date', e.target.value)} />
                  </Field>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <Field label="Status">
                    <select style={input} value={form.status} onChange={(e) => set('status', e.target.value)}>
                      {Object.entries(TASK_STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </Field>
                  <Field label="Priority">
                    <select style={input} value={form.priority} onChange={(e) => set('priority', e.target.value)}>
                      {Object.entries(PRIORITY).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </Field>
                </div>

                {/* People — one searchable combobox to add, then a row per
                    person with their bucket + role (same pattern as the
                    project "Manage people" modal). */}
                {!readOnly && (
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--em-fg2)', marginBottom: 5 }}>Add people</div>
                  <MemberCombobox
                    users={addableUsers}
                    selected={[]}
                    onToggle={togglePerson}
                    placeholder={projectMemberIds ? 'Search project members…' : 'Search people to add…'}
                  />
                  {projectMemberIds && projectMemberIds.size === 0 && (
                    <div style={{ fontSize: 11.5, color: 'var(--em-fg5)', marginTop: 5 }}>
                      This project has no members yet — add people to the project first.
                    </div>
                  )}
                </div>
                )}

                {form.people.length > 0 && (
                  <div>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--em-fg2)', marginBottom: 5 }}>
                      On this task ({form.people.length})
                    </div>
                    <div style={{ border: '1px solid var(--em-border)', borderRadius: 8, overflow: 'hidden', maxHeight: 240, overflowY: 'auto' }}>
                      {form.people.map((p, i) => {
                        const u = users.find((x) => Number(x.id) === p.user_id);
                        const name = personName(u);
                        return (
                          <div key={`${p.user_id}:${p.member_role}`} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderTop: i ? '1px solid var(--em-divider)' : 'none' }}>
                            <span style={{ width: 24, height: 24, borderRadius: '50%', flexShrink: 0, background: avatarColor(name), color: '#fff', fontSize: 10, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                              {initials(name || '?')}
                            </span>
                            <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: 'var(--em-fg1)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {name || `User #${p.user_id}`}
                            </span>
                            <select
                              value={p.member_role}
                              onChange={(e) => setPersonField(i, 'member_role', e.target.value)}
                              style={{ ...miniSelect, width: 116 }}
                            >
                              {TASK_BUCKETS.map((b) => <option key={b.value} value={b.value}>{b.label}</option>)}
                            </select>
                            <select
                              value={p.role}
                              onChange={(e) => setPersonField(i, 'role', e.target.value)}
                              style={{ ...miniSelect, width: 96 }}
                            >
                              <option value="manager">Manager</option>
                              <option value="member">Member</option>
                            </select>
                            <button type="button" title="Remove" onClick={() => removePersonAt(i)}
                              style={{ background: 'none', border: 0, cursor: 'pointer', color: 'var(--em-danger)', display: 'flex', padding: 4 }}>
                              <Trash2 size={14} />
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </fieldset>
            )}

            {tab === 'comments' && editing && (
              <CommentsTab task={task} onChange={applyTask} />
            )}

            {tab === 'files' && editing && (
              <FilesTab task={task} onChange={applyTask} />
            )}

            {tab === 'time' && editing && (
              <TimeTab task={task} onChange={applyTask} />
            )}

            {tab === 'details' && readOnly && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 22 }}>
                <span style={{ fontSize: 12, color: 'var(--em-fg5)' }}>
                  Only the person who created this task can edit it.
                </span>
                <button onClick={onClose} style={ghostBtn}>Close</button>
              </div>
            )}
            {tab === 'details' && !readOnly && (
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10, marginTop: 22 }}>
                <button onClick={onClose} style={ghostBtn}>Cancel</button>
                <button
                  onClick={save}
                  disabled={saving || titleTooLong}
                  title={titleTooLong ? `Shorten the title to ${TASK_TITLE_MAX} characters or fewer` : undefined}
                  style={titleTooLong ? { ...primaryBtn, opacity: 0.55, cursor: 'not-allowed' } : primaryBtn}
                >
                  {saving ? 'Saving…' : editing ? 'Save Changes' : 'Create Task'}
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Member picker (chips) ───────────────────────────────────────────────────

// ── Files tab ───────────────────────────────────────────────────────────────
// Must match middleware/workspaceUpload.js — checked here too so an oversized
// or wrong-typed file is rejected instantly instead of after a full upload.
const MAX_FILE_BYTES = 10 * 1024 * 1024;
const MAX_FILE_LABEL = '10 MB';
const ALLOWED_RE = /\.(jpe?g|png|gif|webp|pdf|docx?|xlsx?|pptx?|txt|csv|zip)$/i;

const fmtSize = (b) =>
  b < 1024 * 1024 ? `${Math.max(1, Math.round(b / 1024))} KB` : `${(b / 1024 / 1024).toFixed(1)} MB`;

// Prefer the server's actual reason over a guess. Two error shapes exist:
// { error: "text" } from controllers, { error: { code, message } } from the
// central handler.
const serverMessage = (err, fallback) => {
  const e = err?.response?.data?.error;
  if (typeof e === 'string') return e;
  if (e && typeof e === 'object') return e.message || fallback;
  return fallback;
};

function FilesTab({ task, onChange }) {
  // { done, total, name, pct } while a batch is going up, else null.
  const [progress, setProgress] = useState(null);
  const fileRef = React.useRef(null);
  const uploading = !!progress;

  // Several files at once: each is checked, then uploaded on its own, one
  // after another — a bad file is reported and skipped, never sinks the rest.
  const uploadMany = async (fileList) => {
    const picked = Array.from(fileList || []);
    if (!picked.length) return;
    const rejected = [];
    const ok = picked.filter((f) => {
      if (f.size > MAX_FILE_BYTES) {
        rejected.push(`"${f.name}" is ${fmtSize(f.size)} — the limit is ${MAX_FILE_LABEL} per file.`);
        return false;
      }
      if (!ALLOWED_RE.test(f.name)) {
        rejected.push(`"${f.name}" isn't a supported file type.`);
        return false;
      }
      return true;
    });
    rejected.forEach((m) => toast.error(m));
    if (fileRef.current) fileRef.current.value = '';
    if (!ok.length) return;

    let uploaded = 0;
    let latest = null;
    for (let i = 0; i < ok.length; i += 1) {
      const file = ok[i];
      setProgress({ done: i, total: ok.length, name: file.name, pct: 0 });
      try {
        latest = await wsApi.uploadAttachment(task.id, file, (p) =>
          setProgress({ done: i, total: ok.length, name: file.name, pct: p }),
        );
        uploaded += 1;
        // Show each file as soon as it lands.
        onChange(latest);
      } catch (err) {
        toast.error(
          serverMessage(
            err,
            err?.response?.status === 413
              ? `The server refused "${file.name}" even in slices — ask an admin to raise its request size limit.`
              : `"${file.name}" failed to upload. Please try again.`,
          ),
        );
      }
    }
    setProgress(null);
    if (uploaded) toast.success(uploaded === 1 ? 'File uploaded' : `${uploaded} files uploaded`);
  };

  const del = async (a) => {
    const ok = await confirmDialog({
      message: `Delete "${a.original_name || 'this file'}"? This can't be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await wsApi.deleteAttachment(a.id);
      onChange(await wsApi.getTask(task.id));
    } catch {
      toast.error('Could not delete file');
    }
  };

  return (
    <div>
      <label style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
        padding: '22px 0', border: '1.5px dashed #d1d5db', borderRadius: 10,
        cursor: uploading ? 'default' : 'pointer', color: 'var(--em-fg5)', fontSize: 13, marginBottom: 14,
      }}>
        <Paperclip size={20} color="#9ca3af" />
        {uploading ? (
          <span style={{ maxWidth: '90%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            Uploading {progress.total > 1 ? `${progress.done + 1} of ${progress.total} · ` : ''}
            {progress.name} — {Math.round(progress.pct * 100)}%
          </span>
        ) : (
          <span><span style={{ color: '#f54b01', fontWeight: 600 }}>Click to upload</span> one or more files</span>
        )}
        <span style={{ fontSize: 11, color: '#9ca3af' }}>PDF, images, docs, sheets — up to {MAX_FILE_LABEL} each</span>
        <input
          ref={fileRef}
          type="file"
          multiple
          disabled={uploading}
          style={{ display: 'none' }}
          onChange={(e) => uploadMany(e.target.files)}
        />
      </label>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 260, overflowY: 'auto' }}>
        {(task.attachments || []).length === 0 && (
          <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: 14 }}>No files attached.</p>
        )}
        {(task.attachments || []).map((a) => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', background: 'var(--em-bg-head)', borderRadius: 8 }}>
            <Paperclip size={14} color="#9ca3af" />
            <a href={assetUrl(a.file_url)} target="_blank" rel="noreferrer" style={{ flex: 1, minWidth: 0, fontSize: 13, color: '#374151', textDecoration: 'none', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {a.original_name || 'file'}
            </a>
            <a href={assetUrl(a.file_url)} download target="_blank" rel="noreferrer" title="Download" style={rowIconBtn}><Download size={14} /></a>
            <button type="button" onClick={() => del(a)} title="Delete file" style={rowIconBtn}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Comments tab ────────────────────────────────────────────────────────────
function CommentsTab({ task, onChange }) {
  const [text, setText] = useState('');
  const [posting, setPosting] = useState(false);

  const post = async () => {
    if (!text.trim()) return;
    setPosting(true);
    try {
      const updated = await wsApi.addComment(task.id, text.trim());
      onChange(updated);
      setText('');
    } catch {
      toast.error('Could not post comment');
    } finally {
      setPosting(false);
    }
  };

  // One colour per commenter, by user id in order of first appearance, so two
  // people in a thread never share a colour (up to the palette size) even when
  // their names are identical — hashing the name gave every "Test" the same one.
  const comments = task.comments || [];
  const colorOf = new Map();
  comments.forEach((c) => {
    const k = String(c.user_id);
    if (!colorOf.has(k)) colorOf.set(k, AVATAR_COLORS[colorOf.size % AVATAR_COLORS.length]);
  });

  return (
    <div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 280, overflowY: 'auto', marginBottom: 14 }}>
        {comments.length === 0 && (
          <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: 20 }}>No comments yet.</p>
        )}
        {comments.map((c) => (
          <div key={c.id} style={{ display: 'flex', gap: 10 }}>
            <span title={personName(c)} style={{ width: 28, height: 28, flexShrink: 0, borderRadius: '50%', background: colorOf.get(String(c.user_id)), color: '#fff', fontSize: 11, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {initials(personName(c))}
            </span>
            <div style={{ flex: 1, minWidth: 0, background: 'var(--em-bg-head)', borderRadius: 8, padding: '8px 12px' }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#111827', overflowWrap: 'anywhere' }}>
                <span title={c.email || undefined}>{personName(c)}</span>
                <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8, fontSize: 11 }}>{fmtDate(c.created_at)}</span>
              </div>
              <div style={{ fontSize: 13, color: '#374151', marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.comment}</div>
            </div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <input style={input} value={text} onChange={(e) => setText(e.target.value)} placeholder="Write a comment…"
          onKeyDown={(e) => e.key === 'Enter' && post()} />
        <button onClick={post} disabled={posting} style={{ ...primaryBtn, padding: '9px 14px' }}><Send size={15} /></button>
      </div>
    </div>
  );
}

// ── Time tab ────────────────────────────────────────────────────────────────
function TimeTab({ task, onChange }) {
  const [mode, setMode] = useState('duration'); // 'duration' | 'range'
  const [hours, setHours] = useState('');
  const [minutes, setMinutes] = useState('');
  const [startAt, setStartAt] = useState(() => toLocalInputValue());
  const [endAt, setEndAt] = useState(() => toLocalInputValue());
  const [desc, setDesc] = useState('');
  const [billable, setBillable] = useState(true);
  const [saving, setSaving] = useState(false);

  // The caller's live timer, kept in step with the header's TimerWidget
  // through the shared `workspaceTimerChanged` event.
  const [timer, setTimer] = useState(null);
  // Refs, so the listener below is registered once and still sees the latest.
  const timerRef = React.useRef(null);
  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;
  // Elapsed = server baseline + local time since it arrived — the server's
  // naive DATETIME is never parsed (JS would read it in the wrong timezone).
  // The anchor is set in the same step as the timer state: setting it in an
  // effect left the first render after Pause/Resume pairing the new paused flag
  // with the old anchor, so the clock flashed a wrong value (resume after a 3s
  // pause showed 13s for 10s) before correcting.
  const anchorRef = React.useRef({ base: 0, since: 0, at: Date.now() });
  const setTimerBoth = useCallback((t) => {
    timerRef.current = t;
    if (t) {
      anchorRef.current = {
        base: Number(t.elapsed_seconds) || 0,
        since: Number(t.since_seconds) || 0,
        at: Date.now(),
      };
    }
    setTimer(t);
  }, []);
  useEffect(() => {
    const refresh = async () => {
      let t;
      try { t = await wsApi.getTimer(); } catch { return; }
      const prev = timerRef.current;
      setTimerBoth(t);
      // This task's timer was stopped elsewhere (the header) → its new entry
      // belongs in the list below.
      if (prev && Number(prev.task_id) === Number(task.id) && (!t || t.id !== prev.id)) {
        wsApi.getTask(task.id).then((u) => onChangeRef.current(u)).catch(() => {});
      }
    };
    refresh();
    window.addEventListener('workspaceTimerChanged', refresh);
    return () => window.removeEventListener('workspaceTimerChanged', refresh);
  }, [task.id, setTimerBoth]);
  const runningHere = !!timer && Number(timer.task_id) === Number(task.id);

  const [, tick] = useState(0);
  useEffect(() => {
    if (!runningHere || timer?.paused) return undefined;
    const id = setInterval(() => tick((n) => n + 1), 1000);
    return () => clearInterval(id);
  }, [runningHere, timer?.paused]);
  const a = anchorRef.current;
  const liveSec = runningHere ? a.base + (timer.paused ? 0 : (Date.now() - a.at) / 1000) : 0;
  const startedAtLabel = runningHere
    ? new Date(a.at - a.since * 1000).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
    : '';

  // logged_minutes / duration_minutes arrive as numbers here, but coerce
  // defensively so the total never string-concatenates. A timer running on
  // this task counts toward the total as it goes.
  const totalLogged =
    (task.time_entries || []).reduce((s, e) => s + (Number(e.duration_minutes) || 0), 0) +
    Math.floor(liveSec / 60);

  // Live preview of the range duration (minutes) — null if invalid.
  const rangeMins = (() => {
    if (mode !== 'range' || !startAt || !endAt) return null;
    const ms = new Date(endAt).getTime() - new Date(startAt).getTime();
    return ms > 0 ? Math.round(ms / 60000) : null;
  })();

  // The smallest entry is 1 minute (the server refuses less) — checked on Log.
  const hoursNum = parseInt(hours, 10) || 0;
  // Live check of what's typed — over 24 hours, negative, minutes > 59.
  const durationError = mode === 'duration' ? durationInputError(hours, minutes) : null;
  // The Start & end range over 24 hours, flagged as soon as it's picked.
  const rangeTooLong = mode === 'range' && rangeMins != null && rangeMins > MAX_ENTRY_HOURS * 60;

  const log = async () => {
    let body = { task_id: task.id, project_id: task.project_id || null, description: desc, billable: billable ? 1 : 0 };
    if (mode === 'range') {
      if (!startAt || !endAt) return toast.error('Pick a start and end time');
      if (rangeMins == null) return toast.error('End time must be after start time');
      // Under 30 seconds rounds to 0 minutes.
      if (rangeMins < 1) return toast.error('The minimum time entry is 1 minute');
      if (rangeTooLong) return toast.error(`A single entry can't be more than ${MAX_ENTRY_HOURS} hours.`);
      body = {
        ...body,
        started_at: localInputToIso(startAt),
        ended_at: localInputToIso(endAt),
        entry_date: localDateStr(new Date(startAt)),
      };
    } else {
      if (durationError) return toast.error(durationError);
      const mins = hoursNum * 60 + (parseInt(minutes, 10) || 0);
      if (mins < 1) return toast.error('The minimum time entry is 1 minute');
      body = { ...body, duration_minutes: mins };
    }
    setSaving(true);
    try {
      await wsApi.addTimeEntry(body);
      const updated = await wsApi.getTask(task.id);
      onChange(updated);
      setHours(''); setMinutes(''); setDesc('');
      setStartAt(toLocalInputValue()); setEndAt(toLocalInputValue());
      toast.success('Time logged');
    } catch (e) {
      toast.error(e?.response?.data?.error || 'Could not log time');
    } finally {
      setSaving(false);
    }
  };

  const del = async (e) => {
    const ok = await confirmDialog({
      message: `Delete this ${fmtDuration(e.duration_minutes)} time entry? This can't be undone.`,
      confirmLabel: 'Delete',
      danger: true,
    });
    if (!ok) return;
    try {
      await wsApi.deleteTimeEntry(e.id);
      onChange(await wsApi.getTask(task.id));
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Could not delete entry');
    }
  };

  const timerChanged = () => window.dispatchEvent(new Event('workspaceTimerChanged'));

  const startTimer = async () => {
    try {
      // No description: a note typed on the running timer becomes the entry's
      // description, and the server falls back to the task title.
      const t = await wsApi.startTimer({ task_id: task.id, project_id: task.project_id || null, description: null });
      setTimerBoth(t);
      timerChanged();
      // A timer that was already running is stopped and logged by the server
      // first — that new entry belongs in any open timesheet list.
      if (t?.stopped) window.dispatchEvent(new Event('workspaceDataChanged'));
      toast.success(t?.stopped ? 'Previous timer stopped and logged — new timer started' : 'Timer started');
    } catch {
      toast.error('Could not start timer');
    }
  };

  const pauseOrResume = async () => {
    try {
      setTimerBoth(await (timer.paused ? wsApi.resumeTimer() : wsApi.pauseTimer()));
      timerChanged();
    } catch {
      toast.error(timer.paused ? 'Could not resume the timer' : 'Could not pause the timer');
    }
  };

  const saveNote = async (note) => {
    if ((note || '').trim() === (timer?.description || '').trim()) return;
    try {
      setTimerBoth(await wsApi.updateTimer({ description: note }));
      timerChanged();
    } catch {
      toast.error('Could not save the note');
    }
  };

  const stopTimer = async (note) => {
    try {
      const res = await wsApi.stopTimer({ description: note });
      setTimerBoth(null);
      onChange(await wsApi.getTask(task.id));
      timerChanged();
      window.dispatchEvent(new Event('workspaceDataChanged'));
      toast.success(`Logged ${fmtDuration(res?.duration_minutes || 0)}`);
    } catch {
      toast.error('Could not stop the timer');
    }
  };

  return (
    <div>
      {!runningHere && (
        <button onClick={startTimer} style={{
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, width: '100%',
          background: '#fff3ec', color: '#c2410c', border: '1px solid #fdba74', borderRadius: 9,
          padding: '10px 0', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 14,
        }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
            <Play size={14} fill="#c2410c" /> Start live timer for this task
          </span>
          {timer && (
            <span style={{ fontSize: 11, fontWeight: 500, color: '#9a3412' }}>
              Stops and logs the timer running on “{timer.description || timer.task_title || timer.project_name || 'another task'}”
            </span>
          )}
        </button>
      )}

      <div style={{ background: 'var(--em-bg-head)', borderRadius: 10, padding: 14, marginBottom: 14 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
          <div style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>Log time manually</div>
          {/* Mode toggle: quick duration vs. explicit start/end time. */}
          <div style={{ display: 'flex', background: 'var(--em-bg-card)', border: '1px solid var(--em-border)', borderRadius: 8, padding: 2 }}>
            {[['duration', 'Duration'], ['range', 'Start & end']].map(([m, label]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                style={{
                  border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer',
                  fontSize: 11.5, fontWeight: 600,
                  background: mode === m ? '#f54b01' : 'transparent',
                  color: mode === m ? '#fff' : '#6b7280',
                }}
              >{label}</button>
            ))}
          </div>
        </div>

        {mode === 'duration' ? (
          <>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
              <input type="number" style={{ ...input, width: 70, ...(durationError ? invalidInput : null) }} placeholder="hrs" value={hours} onChange={(e) => setHours(wrapSpinValue(hours, e.target.value, MAX_ENTRY_HOURS))} aria-invalid={!!durationError} />
              <input type="number" style={{ ...input, width: 70, ...(durationError ? invalidInput : null) }} placeholder="min" value={minutes} onChange={(e) => setMinutes(wrapSpinValue(minutes, e.target.value, 59))} aria-invalid={!!durationError} />
              <input style={{ ...input, flex: 1, minWidth: 120 }} placeholder="What did you work on?" value={desc} onChange={(e) => setDesc(e.target.value)} />
            </div>
            {durationError && (
              <div role="alert" style={{ fontSize: 11.5, color: '#b91c1c', marginTop: 6 }}>{durationError}</div>
            )}
          </>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: '#6b7280', flex: 1, minWidth: 150 }}>
                Start
                <input type="datetime-local" style={input} value={startAt} onChange={(e) => setStartAt(e.target.value)} />
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 3, fontSize: 11, color: '#6b7280', flex: 1, minWidth: 150 }}>
                End
                <input type="datetime-local" style={input} value={endAt} onChange={(e) => setEndAt(e.target.value)} />
              </label>
            </div>
            <input style={input} placeholder="What did you work on?" value={desc} onChange={(e) => setDesc(e.target.value)} />
            <div style={{ fontSize: 11.5, color: rangeMins == null || rangeTooLong ? '#dc2626' : '#6b7280' }}>
              {rangeMins == null
                ? 'End time must be after start time'
                : rangeTooLong
                  ? `Duration: ${fmtDuration(rangeMins)} — a single entry can't be more than ${MAX_ENTRY_HOURS} hours.`
                  : `Duration: ${fmtDuration(rangeMins)}`}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 10 }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5, color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={billable} onChange={(e) => setBillable(e.target.checked)} /> Billable
          </label>
          <button
            onClick={log}
            disabled={saving || !!durationError || rangeTooLong}
            style={{ ...primaryBtn, padding: '7px 14px', ...(durationError || rangeTooLong ? { opacity: 0.55, cursor: 'not-allowed' } : null) }}
          >
            <Plus size={14} /> Log time
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12.5, color: 'var(--em-fg5)', marginBottom: 8 }}>
        <span>Entries</span>
        <span><strong>{fmtDuration(totalLogged)}</strong> total</span>
      </div>
      {runningHere && (
        <RunningTimerCard
          timer={timer}
          elapsedSec={liveSec}
          startedAt={startedAtLabel}
          onPauseResume={pauseOrResume}
          onSaveNote={saveNote}
          onStop={stopTimer}
        />
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 220, overflowY: 'auto' }}>
        {(task.time_entries || []).length === 0 && !runningHere && (
          <p style={{ fontSize: 13, color: '#9ca3af', textAlign: 'center', padding: 14 }}>No time logged yet.</p>
        )}
        {(task.time_entries || []).map((e) => (
          <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--em-bg-head)', borderRadius: 8 }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: '#111827', width: 52 }}>{fmtDuration(e.duration_minutes)}</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.description || '—'}</div>
              <div style={{ fontSize: 11, color: '#9ca3af' }}>{e.username} · {fmtDate(e.entry_date)}</div>
            </div>
            {!!e.billable && <span style={{ fontSize: 10, fontWeight: 600, color: '#065f46', background: '#d1fae5', padding: '2px 7px', borderRadius: 12 }}>Billable</span>}
            <button type="button" onClick={() => del(e)} title="Delete entry" style={rowIconBtn}><Trash2 size={14} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Running timer card (Time tab) ───────────────────────────────────────────
const fmtClock = (totalSec) => {
  const s = Math.max(0, Math.floor(totalSec));
  return [Math.floor(s / 3600), Math.floor((s % 3600) / 60), s % 60]
    .map((n) => String(n).padStart(2, '0'))
    .join(':');
};

// Pause / Stop. Every property the global `button` rule in index.css sets
// (height + line-height 40px, padding, margin) is stated here, or its 40px
// line-height pushes the label below centre.
const timerBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  height: 34, lineHeight: 1, padding: '0 14px', margin: 0, boxSizing: 'border-box',
  borderRadius: 8, cursor: 'pointer', fontSize: 12.5, fontWeight: 600,
};

// The live timer for this task: an editable note (becomes the entry's
// description on Stop; blank → the task title), elapsed time, Pause / Stop.
function RunningTimerCard({ timer, elapsedSec, startedAt, onPauseResume, onSaveNote, onStop }) {
  const [note, setNote] = useState(timer.description || '');
  const [busy, setBusy] = useState(false);
  // A different timer (or one changed from the header) → show its note.
  useEffect(() => { setNote(timer.description || ''); }, [timer.id, timer.description]);

  const run = (fn) => async () => {
    setBusy(true);
    try { await fn(); } finally { setBusy(false); }
  };
  const paused = !!timer.paused;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', marginBottom: 8,
      background: paused ? '#f9fafb' : '#ecfdf5', border: `1px solid ${paused ? '#e5e7eb' : '#a7f3d0'}`, borderRadius: 10,
    }}>
      <span style={{
        width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
        background: paused ? '#9ca3af' : '#10b981', animation: paused ? 'none' : 'wsPulse 1.2s infinite',
      }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <input
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={() => onSaveNote(note)}
          onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
          placeholder="Add a note..."
          maxLength={500}
          style={{
            width: '100%', border: 'none', outline: 'none', background: 'transparent', padding: 0,
            fontSize: 13.5, color: '#111827', fontFamily: 'inherit',
          }}
        />
        <div style={{ fontSize: 11.5, color: paused ? '#6b7280' : '#059669', marginTop: 2 }}>
          {paused ? 'Paused' : 'Recording'} · since {startedAt}{timer.billable ? ' · Billable' : ''}
        </div>
      </div>
      <span style={{
        fontSize: 22, fontWeight: 700, color: paused ? '#6b7280' : '#065f46',
        fontVariantNumeric: 'tabular-nums', letterSpacing: '0.02em',
      }}>
        {fmtClock(elapsedSec)}
      </span>
      {/* preventDefault on mousedown: the note input keeps focus, so its blur
          save can't race Stop (which sends the note itself). */}
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={run(onPauseResume)}
        disabled={busy}
        style={{ ...timerBtn, border: '1px solid #a7f3d0', background: '#fff', color: '#065f46' }}
      >
        {paused ? 'Resume' : 'Pause'}
      </button>
      <button
        onMouseDown={(e) => e.preventDefault()}
        onClick={run(() => onStop(note))}
        disabled={busy}
        style={{ ...timerBtn, border: '1px solid #065f46', background: '#065f46', color: '#fff' }}
      >
        Stop
      </button>
      <style>{'@keyframes wsPulse{0%,100%{opacity:1}50%{opacity:.3}}'}</style>
    </div>
  );
}

// ── shared styles (mirrors ProjectsList) ────────────────────────────────────
function Field({ label, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: '#374151' }}>{label}</span>
      {children}
    </label>
  );
}
const input = { width: '100%', border: '1px solid var(--em-border)', borderRadius: 8, padding: '9px 11px', fontSize: 13.5, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' };
// Red border + tint for an input whose value fails validation.
const invalidInput = { borderColor: '#ef4444', background: '#fef2f2' };
// Compact select used inside a people row (with the shared dropdown chevron).
const miniSelect = {
  flexShrink: 0, border: '1px solid var(--em-border)', borderRadius: 7,
  padding: '5px 8px', fontSize: 12, fontFamily: 'inherit',
  background: 'var(--em-bg-card)', color: 'var(--em-fg1)',
  ...selectChevron, paddingRight: 22,
};
const overlay = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 };
const modal = { background: 'var(--em-bg-card)', borderRadius: 14, padding: '22px 24px', width: '100%', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 24px 64px rgba(0,0,0,0.2)' };
const modalHead = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 18 };
// color is set on purpose: the global `button:hover` rule (index.css) turns
// button text white, and these icons draw in currentColor — without it the
// ✕ vanished on hover against the white modal.
const iconBtn = { background: 'none', border: 'none', cursor: 'pointer', display: 'flex', padding: 4, color: 'var(--em-fg1)' };
// Download / delete icons on a file or time-entry row. The same box for the
// <a> and the <button>, and the global `button` rule's 40px height, 40px
// line-height and 4px margin reset — they made the bin sit lower and further
// out than the download icon beside it.
const rowIconBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  width: 24, height: 24, padding: 0, margin: 0, lineHeight: 1, boxSizing: 'border-box',
  background: 'none', border: 'none', borderRadius: 6, cursor: 'pointer', color: '#9ca3af',
};
const primaryBtn = { display: 'flex', alignItems: 'center', gap: 7, background: '#f54b01', color: '#fff', border: 'none', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
const ghostBtn = { display: 'flex', alignItems: 'center', gap: 7, background: 'var(--em-bg-card)', color: '#374151', border: '1px solid var(--em-border)', borderRadius: 8, padding: '9px 16px', cursor: 'pointer', fontSize: 13, fontWeight: 600 };
